import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import moment from 'moment';
import { SortEnum } from 'src/app/utils/enums/sort-enum';
import { Filtres } from 'src/app/utils/interfaces/new/filtres.interface';
import { ImpressionOrdoHisto } from 'src/app/utils/interfaces/new/ImpressionOrdoHisto.interface';
import { Ordonnance } from 'src/app/utils/interfaces/new/ordonnance';
import { LigneOrdonnance } from 'src/app/utils/interfaces/new/ligne-ordonnance';
import { OrdonnanceLibre } from 'src/app/utils/interfaces/new/ordonnance-libre';
import { Paginated } from 'src/app/utils/interfaces/new/paginated.interface';
import { environment } from 'src/environments/environment';
import { FiltresService } from '../../filtres/filtres.service';
import { SnackbarService } from '../../snackbar/snackbar.service';
import { PrescriptionActesMedicaux } from 'src/app/utils/interfaces/new/prescription-actes-medicaux';
import { LigneActe } from 'src/app/utils/interfaces/new/ligne-acte';
import { RessourceService } from '../../ressource/ressource.service';
import { RessourceTypeEnum } from 'src/app/utils/interfaces/new/RessourceSetting';

@Injectable({
  providedIn: 'root'
})
export class OrdonnancesService {
  private readonly defaultHttpOptions = {
    withCredentials: true,
    observe: 'response' as 'response',
    headers: new HttpHeaders(),
    params: undefined
  };

  private apiUri = `${environment.basePath}/consultation/ordonnances`;

  constructor(
    private _http: HttpClient,
    private _filtresService: FiltresService,
    private _snack: SnackbarService,
    private _resourceSrv: RessourceService,
  ) { }

  public async findOrdonnanceMedicamenteuse(filtre: Filtres = {
    perpage: -1,
    currentPage: 1,
    sortOrder: SortEnum.ASC,
    sortBy: 'dateCreation'
  }, idPatient: string, consultationId?: string, creationDate?: Date): Promise<Paginated<Ordonnance>> {
    try {
      let httpOptions = {...this.defaultHttpOptions};
      httpOptions.headers = this._filtresService.setHeaders(filtre);

      if (consultationId || creationDate) {
        httpOptions.params = new HttpParams();
      }
      if (consultationId) {
        httpOptions.params = httpOptions.params.set('consultationId', consultationId);
      }
      if (creationDate) {
        // URLEncode de la date de création au format date-time
        httpOptions.params = httpOptions.params.set('creationDate', moment(creationDate).format("YYYY-MM-DD"));
      }

      const res = await this._http.get(`${this.apiUri}/${idPatient}`, httpOptions).toPromise();
      const ordonnances = res.body as Ordonnance[];
      const paginated: Paginated<Ordonnance> = {
        page: Number(res.headers.get('x-page')),
        perpage: Number(res.headers.get('x-per-page')),
        total: Number(res.headers.get('x-total')),
        objects: ordonnances
      };
      return paginated;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  /**
   *
   * @param filtre
   * @param idPatient
   * @param consultationId
   * @returns
   */
  public async findOrdonnanceMedicamenteuseFromLastConsultation(idPatient: string, consultationId: string): Promise<Paginated<Ordonnance>> {
    try {
      let httpOptions = {...this.defaultHttpOptions};

      httpOptions.params = new HttpParams();
      httpOptions.params = httpOptions.params.set('notConsultationId', consultationId);

      const res = await this._http.get(`${this.apiUri}/last-consultation/${idPatient}`, httpOptions).toPromise();
      const ordonnances = res.body as Ordonnance[];
      const paginated: Paginated<Ordonnance> = {
        page: Number(res.headers.get('x-page')),
        perpage: Number(res.headers.get('x-per-page')),
        total: Number(res.headers.get('x-total')),
        objects: ordonnances
      };
      return paginated;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async findOneOrdonnanceMedicamenteuse(id: string) {
    try {
      const res = await this._http.get(`${this.apiUri}/infos/${id}`, this.defaultHttpOptions).toPromise();
      const ordonnances = res.body as Ordonnance;
      return ordonnances;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async createOrdonnanceMedicamenteuse(ordonnance: Ordonnance) {
    try {
      ordonnance.isEprescription = !!ordonnance.isEprescription;

      if (ordonnance.ligne && ordonnance.ligne.length > 0) {
        for (const produit of ordonnance.ligne) {
          this.normalizeLigneOrdonnance(produit);
        }
      }

      const res = await this._http.post(`${this.apiUri}`, ordonnance, this.defaultHttpOptions).toPromise();
      return res.body as string;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async deleteOrdonnanceMedicamenteuse(ordonnanceId: string) {
    try {
      const res = await this._http.delete(`${this.apiUri}/infos/${ordonnanceId}`, this.defaultHttpOptions).toPromise();
      return;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async deleteOrdonnanceLigneMedicamenteuse(ordonnanceLigneId: string) {
    try {
      await this._http.delete(`${this.apiUri}/ligne/${ordonnanceLigneId}`, this.defaultHttpOptions).toPromise();
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async deletePosologie(id: string) {
    try {
      await this._http.delete(`${this.apiUri}/ligne/posologie/${id}`, this.defaultHttpOptions).toPromise();
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async updateOrdonnanceMedicamenteuse(ordonnance: Ordonnance) {
    try {
      if (ordonnance.ligne && ordonnance.ligne.length > 0) {
        for (const produit of ordonnance.ligne) {
          this.normalizeLigneOrdonnance(produit);
        }
      }
      const res = await this._http.put(`${this.apiUri}/infos/${ordonnance.id}`, ordonnance, this.defaultHttpOptions).toPromise();
      return;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async findOrdonnanceLibre(filtre: Filtres = {
    perpage: -1,
    currentPage: 1,
    sortOrder: SortEnum.ASC,
    sortBy: 'dateCreation'
  }, idPatient: string, consultationId?: string): Promise<Paginated<OrdonnanceLibre>> {
    try {
      let httpOptions = {...this.defaultHttpOptions};
      httpOptions.headers = this._filtresService.setHeaders(filtre);

      if (consultationId) {
        httpOptions.params = (httpOptions.params || new HttpParams())
          .set('consultationId', consultationId.toString());
      }

      const res = await this._http.get(`${this.apiUri}/libre/${idPatient}`, httpOptions).toPromise();
      const ordonnances = res.body as OrdonnanceLibre[];
      const paginated: Paginated<OrdonnanceLibre> = {
        page: Number(res.headers.get('x-page')),
        perpage: Number(res.headers.get('x-per-page')),
        total: Number(res.headers.get('x-total')),
        objects: ordonnances
      };
      return paginated;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async findOneOrdonnanceLibre(id: string) {
    try {
      const res = await this._http.get(`${this.apiUri}/libre/infos/${id}`, this.defaultHttpOptions).toPromise();
      const ordonnanceLibre = res.body as OrdonnanceLibre;
      return ordonnanceLibre;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async createOrdonnanceLibre(ordonnance: OrdonnanceLibre) {
    try {
      const res = await this._http.post(`${this.apiUri}/libre`, ordonnance, this.defaultHttpOptions).toPromise();
      return res.body as string;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
      throw error;
    }
  }

  public async deleteOrdonnanceLibre(ordonnanceLibreId: string) {
    try {
      const res = await this._http.delete(`${this.apiUri}/libre/infos/${ordonnanceLibreId}`, this.defaultHttpOptions).toPromise();
      return;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async deleteLigneOrdonnanceLibre(ordonnanceLigneLibreId: string) {
    try {
      const res = await this._http.delete(`${this.apiUri}/libre/ligne/${ordonnanceLigneLibreId}`, this.defaultHttpOptions).toPromise();
      return;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async updateOrdonnanceLibre(ordonnanceLibre: OrdonnanceLibre) {
    try {
      const res = await this._http.put(`${this.apiUri}/libre/infos/${ordonnanceLibre.id}`, ordonnanceLibre, this.defaultHttpOptions).toPromise();
      return;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async histoPrintOrdonnance(printHisto: ImpressionOrdoHisto) {
    try {
      const res = await this._http.post(`${this.apiUri}/print`, printHisto, this.defaultHttpOptions).toPromise();
      return res.body as string;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async getDocumentByHisto(id: string): Promise<string | PromiseLike<string>> {
    try {
      const res = await this._http.get(`${this.apiUri}/print/${id}`, this.defaultHttpOptions).toPromise();
      return res.body as string;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async deleteHisto(id: string) {
    try {
      await this._http.delete(`${this.apiUri}/print/${id}`, this.defaultHttpOptions).toPromise();
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  async updateHistoOrdonnance(id: string, updateData: Partial<ImpressionOrdoHisto>) {
    try {
      const res = await this._http.put(`${this.apiUri}/print/${id}`, updateData, this.defaultHttpOptions).toPromise();
      return res.body;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
      throw error;
    }
  }

  findHistoOrdo(_patientId: string, title: string, dateStart: string, dateEnd: string, typeEprescription: string, _tableFiltre: Filtres): Paginated<ImpressionOrdoHisto> | PromiseLike<Paginated<ImpressionOrdoHisto>> {
    return new Promise<Paginated<ImpressionOrdoHisto>>(async (resolve, reject) => {
      try {
        let httpOptions = {...this.defaultHttpOptions};
        httpOptions.headers = this._filtresService.setHeaders(_tableFiltre);

        const query = {};

        if (title && title !== '') {
          query['title'] = title;
        }

        if (dateStart) {
          query['dateStart'] = encodeURIComponent(moment.utc(dateStart).toISOString());
        }

        if (dateEnd) {
          query['dateEnd'] = encodeURIComponent(moment.utc(dateEnd).toISOString());
        }

        if (typeEprescription) {
          query['typeEprescription'] = typeEprescription;
        }

        httpOptions.params = query;

        const res = await this._http.get(`${this.apiUri}/print/patient/${_patientId}`, httpOptions).toPromise();
        const mails = res.body as Array<ImpressionOrdoHisto>;
        const paginated: Paginated<ImpressionOrdoHisto> = {
          page: Number(res.headers.get('x-page')),
          perpage: Number(res.headers.get('x-per-page')),
          total: Number(res.headers.get('x-total')),
          objects: mails
        };
        resolve(paginated);
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  // Prescription d'actes

  public async findPrescriptionActesMedicaux(filtre: Filtres = {
    perpage: -1,
    currentPage: 1,
    sortOrder: SortEnum.ASC,
    sortBy: 'dateCreation'
  },
  idPatient: string,
  idConsultation?: string): Promise<Paginated<PrescriptionActesMedicaux>> {
    try {
      let httpOptions = {...this.defaultHttpOptions};
      httpOptions.headers = this._filtresService.setHeaders(filtre);
      let url = `${this.apiUri}/actesMedicaux?idPatient=${idPatient}`;
      if (idConsultation) {
        url += `&idConsultation=${idConsultation}`;
      }
      const res = await this._http.get(url, httpOptions).toPromise();
      const prescriptionActe = res.body as PrescriptionActesMedicaux[];
      const paginated: Paginated<PrescriptionActesMedicaux> = {
        page: Number(res.headers.get('x-page')),
        perpage: Number(res.headers.get('x-per-page')),
        total: Number(res.headers.get('x-total')),
        objects: prescriptionActe
      };
      return paginated;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async findOnePrescriptionActesMedicaux(prescriptionId: string): Promise<PrescriptionActesMedicaux>  {
    try {
      const res = await this._http.get(`${this.apiUri}/actesMedicaux/${prescriptionId}`, this.defaultHttpOptions).toPromise();
      const prescriptionActe = res.body as PrescriptionActesMedicaux;
      return prescriptionActe;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async createPrescriptionActesMedicaux(prescription: Partial<PrescriptionActesMedicaux>) {
    try {
      const res = await this._http.post(`${this.apiUri}/actesMedicaux`, prescription, this.defaultHttpOptions).toPromise();
      return res.body as string;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
      throw error;
    }
  }

  public async deletePrescriptionActesMedicaux(prescriptionId: string) {
    try {
      const res = await this._http.delete(`${this.apiUri}/actesMedicaux/${prescriptionId}`, this.defaultHttpOptions).toPromise();
      return;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }
  public async createLignePrescriptionActesMedicaux(prescriptionLigne: Partial<LigneActe>, previousId?: string) {
    try {
      let url = `${this.apiUri}/actesMedicaux/${prescriptionLigne.idPrescriptionActesMedicaux}/ligne`;
      if (previousId) {
        url += `?previousId=${previousId}`;
      }
      const res = await this._http.post(url, prescriptionLigne, this.defaultHttpOptions).toPromise();
      return res.body as string;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
      throw error;
    }
  }
  public async deleteLignePrescriptionActesMedicaux(prescriptionId: string, prescriptionActeLigneId: string) {
    try {
      const res = await this._http.delete(`${this.apiUri}/actesMedicaux/${prescriptionId}/ligne/${prescriptionActeLigneId}`, this.defaultHttpOptions).toPromise();
      return;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async updatePrescriptionActesMedicaux(prescription: PrescriptionActesMedicaux) {
    try {
      const res = await this._http.put(`${this.apiUri}/actesMedicaux/${prescription.id}`, prescription, this.defaultHttpOptions).toPromise();
      return;
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async getOrdonnanceImpressionParams(profilId: string): Promise<OrdonnanceImpressionParams> {
    try {
      const profilParams = await this._resourceSrv.getResourceSettings(profilId, 'profil');
      const impressionProduits = this._resourceSrv.getSettingFromArray(profilParams, OrdonnanceImpressionProduit);
      const identificationPatient = this._resourceSrv.getSettingFromArray(profilParams, OrdonnanceImpressionIdentificationPatient);
      return {
        impressionProduits: impressionProduits ? impressionProduits.value as OrdonnanceImpressionProduitEnum : undefined,
        identificationPatient: identificationPatient ? identificationPatient.value as OrdonnanceImpressionIdentificationPatientEnum : undefined,
      };
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  public async setOrdonnanceImpressionParams(profilId: string, params: OrdonnanceImpressionParams): Promise<void> {
    try {
      await this._resourceSrv.postResourceSettings(profilId, 'profil', {
        ressourceId: profilId,
        ressourceType: RessourceTypeEnum.Profil,
        settingKey: OrdonnanceImpressionProduit,
        settingValue: params.impressionProduits,
        settingType: 'enum'
      });
      await this._resourceSrv.postResourceSettings(profilId, 'profil', {
        ressourceId: profilId,
        ressourceType: RessourceTypeEnum.Profil,
        settingKey: OrdonnanceImpressionIdentificationPatient,
        settingValue: params.identificationPatient,
        settingType: 'enum'
      });
    } catch (error) {
      this._snack.openSnackBar(error.error.message);
    }
  }

  private normalizeLigneOrdonnance(produit: LigneOrdonnance): void {
    const stringKeys: (keyof LigneOrdonnance)[] = [
      'id', 'idOrdonnanceMedicamenteuse', 'idProduit', 'labelProduit',
      'idDcMolecule', 'libelleDcMolecule', 'ingredientProduit',
      'atcNiveau1', 'atcNiveau2', 'atcNiveau3', 'atcNiveau4', 'atcNiveau5',
      'motifNonSubstitution', 'quantiteTotale', 'uniteBoite',
      'dateDebutPrise', 'dateFinPrise', 'commentaireDebut', 'commentaireFin',
      'pharmacieDelivrante', 'renouvellement', 'posologie',
      'prix', 'prixLigne', 'motif', 'typeMotif', 'cim10',
      'codeAld', 'codeMedicabase', 'moleculeEprescription',
      'codeLpp', 'acl', 'ref13', 'libelleMarque', 'conditionnementQuantite',
      'produitLibreTitre', 'cip', 'detailsPreparationMagistrale', 'type'
    ];
    for (const key of stringKeys) {
      if (produit[key] != null) {
        (produit as unknown as Record<string, unknown>)[key] = String(produit[key]);
      }
    }
  }
}

export interface OrdonnanceImpressionParams {
  impressionProduits?: OrdonnanceImpressionProduitEnum;
  identificationPatient?: OrdonnanceImpressionIdentificationPatientEnum;
}

export enum OrdonnanceImpressionProduitEnum {
  IDENTIQUE_EXISTANT = 'IDENTIQUE_EXISTANT',
  DCI_NOM_PRODUIT = 'DCI_NOM_PRODUIT',
  DCI = 'DCI',
}

export enum OrdonnanceImpressionIdentificationPatientEnum {
  SANS_INCLURE_OUI = 'SANS_INCLURE_OUI',
  SANS_INCLURE_NON = 'SANS_INCLURE_NON',
}

export const OrdonnanceImpressionProduit = 'ordonnance_impression_produit';
export const OrdonnanceImpressionIdentificationPatient = 'ordonnance_impression_identification_patient';
